import { OptionType } from "@portal/BaseComponents/CheckBox/type";
import { MultiSelectElement } from "@portal/BaseComponents/Filters/Elements/MultiSelect/MultiSelectElement";
import { FilterDropdownDown } from "@portal/BaseComponents/Filters/FilterDropdown/FilterDropdownDown";
import { getMultiSelectDisplayValue } from "@portal/BaseComponents/Filters/utils/multiSelectDisplayUtils";
import useOpen from "@portal/hooks/useOpen";
import { useDisputeTypesQuery } from "@portal/queries/dispute-query";
import { disputeFiltersAtom } from "@portal/store/Filters/dipsutes/dispute-filter";
import { resetPage, tableAtom } from "@portal/store/table/table";
import { DisputeTypes } from "@portal/types/entities/dispute";
import { stringConverterPredicate } from "@portal/util/predicates";
import produce from "immer";
import { useAtom, useSetAtom } from "jotai";
import { useMemo } from "react";

const disputeOptionsPredicate = (type: DisputeTypes): OptionType => {
  return { label: type.name, value: type.id };
};

function DisputeTypesFilter() {
  const { onClose, open, toggleOpen, handleOpenChange } = useOpen({
    open: false,
  });
  const { data, isLoading } = useDisputeTypesQuery({ include_archived: true });
  const setTable = useSetAtom(tableAtom);
  const [disputeFilters, setDisputeFilters] = useAtom(disputeFiltersAtom);

  function handleHideReset() {
    return !disputeFilters.filterValues.dispute_type_ids?.length;
  }
  function getDisputeTypesOptions(): OptionType[] {
    return data ? data.list.map(disputeOptionsPredicate) : [];
  }
  function handleReset() {
    onClose();
    setDisputeFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.dispute_type_ids = undefined;
        return draft;
      });
    });
    setTable(resetPage);
  }

  function handleSubmit(value: OptionType["value"][]) {
    setDisputeFilters((filter) => {
      return produce(filter, (draft) => {
        draft.filterValues.dispute_type_ids = value.map(
          stringConverterPredicate
        );
        return draft;
      });
    });
    onClose();
  }
  const options = useMemo(getDisputeTypesOptions, [data]);

  const Content = (
    <MultiSelectElement
      multiSelectProps={{
        initialValue: disputeFilters.filterValues.dispute_type_ids,
        options,
        onSubmit: handleSubmit,
        onCancel: onClose,
        loading: isLoading,
      }}
      search={{
        type: "local",
        placeholder: "Search Dispute Types",
      }}
    />
  );

  function getSelectedValues() {
    return getMultiSelectDisplayValue({
      selectedValues: disputeFilters.filterValues.dispute_type_ids ?? [],
      allOptions: options.map((opt) => String(opt.value)),
      labelMapper: (val) => {
        const option = options.find((opt) => String(opt.value) === val);
        return String(option?.label ?? val);
      },
    });
  }

  const selectedFilterText = useMemo(getSelectedValues, [
    disputeFilters.filterValues.dispute_type_ids,
    options,
  ]);

  const hideReset = handleHideReset();
  return (
    <FilterDropdownDown
      filterBlockProps={{
        isDefault: true,
        toggleOpen,
        open,
        title: "Dispute Types",
        filterValues: selectedFilterText,
        onReset: handleReset,
        hideReset,
      }}
      filterComponent={Content}
      filterTitle={"Dispute Types"}
      onopenChange={handleOpenChange}
    />
  );
}

export { DisputeTypesFilter };
